import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { eventDetails } from "@/mocks/eventDetails";
import { events, hotTopics } from "@/mocks/site";
import type { EventDetail } from "@/types/content";
import { formatDate, formatHeat } from "@/utils/format";

export default function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const event = events.find((e) => e.slug === slug);
  const detail = slug ? (eventDetails as Record<string, EventDetail>)[slug] : undefined;

  const [activeId, setActiveId] = useState("key-points");
  const observed = useRef<string[]>([]);

  const toc = useMemo(() => {
    if (!detail) return [];
    const items = [
      { id: "key-points", label: "线索要点" },
      { id: "timeline", label: "时间线" },
    ];
    detail.sections.forEach((section, i) => items.push({ id: `section-${i}`, label: section.heading }));
    return items;
  }, [detail]);

  useEffect(() => {
    observed.current = toc.map((t) => t.id);
    if (observed.current.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -60% 0px", threshold: [0, 1] },
    );
    observed.current.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [toc]);

  const related = useMemo(() => {
    if (!detail) return [];
    return detail.related
      .map((s) => events.find((e) => e.slug === s))
      .filter((e): e is (typeof events)[number] => Boolean(e));
  }, [detail]);

  useSeo({
    title: event ? `${event.title} - 独家吃瓜事件复盘` : "档案未找到 - 独家吃瓜",
    description: event
      ? `${event.excerpt} 本文按时间线梳理该事件的独家吃瓜深扒内容，包含线索要点、各方回应与细节复盘。`
      : "该事件档案暂未收录，可返回档案库浏览其他事件复盘。",
    keywords: event ? [`独家吃瓜`, ...event.tags, "事件复盘", "时间线"].join(",") : "独家吃瓜,事件复盘",
    canonicalPath: `/archive/${slug ?? ""}`,
    jsonLd: event
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: event.title,
              description: event.excerpt,
              articleSection: event.category,
              datePublished: event.date,
              author: { "@type": "Person", name: event.author },
              publisher: { "@type": "Organization", name: "独家吃瓜" },
              keywords: event.tags.join(","),
              image: event.image,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "/" },
                { "@type": "ListItem", position: 2, name: "事件档案", item: "/archive" },
                { "@type": "ListItem", position: 3, name: event.title, item: `/archive/${event.slug}` },
              ],
            },
          ],
        }
      : null,
  });

  if (!event || !detail) {
    return (
      <>
        <SiteHeader />
        <main className="container-x flex min-h-[56vh] flex-col items-center justify-center py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-search-line text-2xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条档案还没建好</h1>
          <p className="mt-3 max-w-md text-sm leading-7 text-foreground-600">
            该事件可能尚未归档，或链接已经调整。先回到档案库看看其他复盘，也可以把这条线索投递给我们。
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/archive"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回档案库
            </Link>
            <Link
              to="/about#contact"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              投递线索
            </Link>
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <SiteHeader />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="container-x py-8 md:py-12">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "事件档案", to: "/archive" },
                { label: event.category, to: `/archive?category=${event.categorySlug}` },
                { label: event.title },
              ]}
            />
            <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-price-tag-3-line"></i>
              {event.category}
            </span>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl md:leading-tight">
              {event.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line"></i>
                {event.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {formatDate(event.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读约 {event.readTime}
              </span>
              <span className="flex items-center gap-1.5 text-primary-600">
                <i className="ri-fire-line"></i>
                热度 {formatHeat(event.heat)}
              </span>
            </div>
          </div>
        </div>

        <div className="container-x py-10 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
                <img
                  src={event.image}
                  alt={`${event.title} — 独家吃瓜事件复盘主图`}
                  title={`${event.title} 独家吃瓜事件复盘`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <p className="mt-7 border-l-4 border-primary-400 bg-background-100 px-5 py-5 font-heading text-base leading-8 text-foreground-800">
                {detail.lead}
              </p>

              <section id="key-points" className="mt-10 scroll-mt-28">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <i className="ri-focus-3-line text-primary-600"></i>
                  线索要点
                </h2>
                <ul className="mt-5 space-y-3">
                  {detail.keyPoints.map((point, i) => (
                    <li key={point} className="flex items-start gap-3 rounded-lg bg-background-100 px-4 py-4">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-500 text-xs font-bold text-foreground-950">
                        {i + 1}
                      </span>
                      <span className="text-sm leading-7 text-foreground-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section id="timeline" className="mt-10 scroll-mt-28">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <i className="ri-git-commit-line text-primary-600"></i>
                  时间线
                </h2>
                <ol className="relative mt-6 border-l-2 border-background-200 pl-6">
                  {detail.timeline.map((point) => (
                    <li key={point.time} className="relative pb-7 last:pb-0">
                      <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-background-100 bg-secondary-500"></span>
                      <p className="font-heading text-sm font-bold text-secondary-800">{point.time}</p>
                      <p className="mt-1.5 text-sm leading-7 text-foreground-700">{point.text}</p>
                    </li>
                  ))}
                </ol>
              </section>

              {detail.sections.map((section, i) => (
                <section key={section.heading} id={`section-${i}`} className="mt-10 scroll-mt-28">
                  <h2 className="font-heading text-xl font-bold text-foreground-950">{section.heading}</h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((p) => (
                      <p key={p.slice(0, 24)} className="text-sm leading-8 text-foreground-700 md:text-base md:leading-9">
                        {p}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              <div className="mt-10 flex flex-wrap gap-2 border-t border-background-200 pt-6">
                {event.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/archive?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-50 px-3 py-1.5 text-xs text-secondary-800 transition-colors hover:bg-secondary-100"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-8 rounded-lg border border-accent-300 bg-accent-50 p-5">
                <p className="flex items-start gap-3 text-sm leading-7 text-foreground-700">
                  <i className="ri-information-line mt-0.5 text-lg text-accent-800"></i>
                  <span>
                    本文内容基于公开信息整理，仅用于梳理事件脉络。若后续出现新的公开回应，我们会在时间线中补充更新。
                  </span>
                </p>
              </div>

              {related.length > 0 && (
                <section className="mt-12">
                  <h2 className="font-heading text-xl font-bold text-foreground-950">相关阅读</h2>
                  <ul className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {related.map((item) => (
                      <li key={item.slug}>
                        <Link
                          to={`/archive/${item.slug}`}
                          className="group block h-full overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                        >
                          <div className="h-28 w-full overflow-hidden bg-background-200">
                            <img
                              src={item.image}
                              alt={`${item.title} — 相关复盘配图`}
                              title={`${item.title} 独家吃瓜`}
                              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                          <div className="p-4">
                            <span className="text-xs text-foreground-500">{item.category}</span>
                            <p className="mt-1.5 font-heading text-sm font-bold leading-6 text-foreground-950 transition-colors group-hover:text-primary-600">
                              {item.title}
                            </p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-6">
                <nav className="rounded-lg border border-background-200 bg-background-100 p-5" aria-label="本页目录">
                  <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                    <i className="ri-list-check-2 text-primary-600"></i>
                    本页目录
                  </h2>
                  <ul className="mt-4 space-y-1">
                    {toc.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className={`block rounded-md px-3 py-2 text-sm transition-colors ${
                            activeId === item.id
                              ? "bg-background-50 font-medium text-primary-600"
                              : "text-foreground-600 hover:bg-background-50"
                          }`}
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">档案信息</h2>
                  <dl className="mt-4 space-y-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-foreground-500">分类</dt>
                      <dd>
                        <Link
                          to={`/archive?category=${event.categorySlug}`}
                          className="text-foreground-800 transition-colors hover:text-primary-600"
                        >
                          {event.category}
                        </Link>
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-foreground-500">归档日期</dt>
                      <dd className="text-foreground-800">{event.date}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-foreground-500">整理人</dt>
                      <dd className="text-foreground-800">{event.author}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-foreground-500">时间节点</dt>
                      <dd className="text-foreground-800">{detail.timeline.length} 个</dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">正在复盘</h2>
                  <ul className="mt-4 space-y-2.5">
                    {hotTopics.slice(0, 5).map((topic) => (
                      <li key={topic.slug}>
                        <Link
                          to={`/archive/${topic.slug}`}
                          className="flex items-center justify-between gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                        >
                          <span className="truncate">{topic.label}</span>
                          <i className="ri-arrow-right-s-line shrink-0 text-foreground-400"></i>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}